import React, { useEffect, useContext } from 'react'
import { withRouter } from 'react-router'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'

import { fetchTourV2Offer } from 'actions/TourV2Actions'
import { getTourV2CartItems, getTourV2ShareModalConfig } from 'selectors/TourV2/tourV2OfferPageSelectors'
import { ShareModalConfig } from 'lib/customer/getShareModalConfig'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import { INITIAL_OFFER_PAGE_STATE, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TourV2Content from 'components/OfferPage/TourV2/TourV2Content'
import { ENABLED_OFFER_TYPES_RECENTLY_VIEWED } from 'lib/offer/highIntentOffers'
import { offerViewed } from 'actions/OfferActions'
import { tourV2OfferProductDetailView } from 'actions/AnalyticsActions'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import GeoContext from 'contexts/geoContext'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'

import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { tourOfferClicked } from 'analytics/eventDefinitions'
import useQueryParams from 'hooks/useQueryParams'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import * as Analytics from 'analytics/analytics'
import { usePageActivityTracking } from 'analytics/snowplow/helpers/useActivityTracking'
import useOfferPageWalledGarden from 'hooks/OfferPage/useOfferPageWalledGarden'
import { min } from 'lib/array/arrayUtils'

const OfferPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

interface MappedStateProps {
  checkoutItems: Array<App.Checkout.TourV2Item>
  isOfferLoading?: boolean
  offer?: Tours.TourV2Offer
  offerError?: any
  shareModalConfig: ShareModalConfig
  currency: string
  isPrivateRequest: boolean
}

type Props = RouteComponentProps<{ offerId: string, variationId: string }>

function TourV2OfferVariationPage(props: Props & MappedStateProps) {
  const {
    checkoutItems,
    isOfferLoading,
    isPrivateRequest,
    match,
    offer,
    offerError,
    shareModalConfig,
    currency,
  } = props
  const { offerId, variationId } = match.params
  const geo = useContext(GeoContext)
  const dispatch = useAppDispatch()

  useOfferPageWalledGarden()

  const [offerPageState, offerPageDispatch, stateSnapshotRef] = useOfferPageReducer({
    ...INITIAL_OFFER_PAGE_STATE,
    tourV2Offer: offer,
    tourV2Variation: offer?.variations[variationId],
    displayMemberPrice: !!offer?.luxPlus.hasMemberPrices,
  })
  const queryParams = useQueryParams()

  useEffect(() => {
    if (offer) {
      fireEcommerceGTMEvent(tourOfferClicked(offer, currency))
    }
  }, [currency, offer])

  usePageActivityTracking()

  useEffect(() => {
    const privateRequestKey = queryParams.get('privateRequestKey') ?? undefined
    dispatch(fetchTourV2Offer(offerId, privateRequestKey))
  }, [dispatch, offerId, queryParams])

  useEffect(() => {
    if (offer) {
      const variation = offer.variations[variationId]
      offerPageDispatch({
        type: OfferPageActions.setTourV2Offer,
        variation,
        offer,
      })

      if (offer.luxPlus.hasMemberPrices) {
        dispatch({ type: OfferPageActions.setDisplayMemberPrice, displayMemberPrice: true })
      }

      const availableDepartures = variation.departures.filter(departure => departure.options.length > 0)
      const earliestDate = min(variation.departures.map(departure => new Date(departure.startDate)))
      const earliestAvailableDate = min(availableDepartures.map(departure => new Date(departure.startDate)))

      if (earliestAvailableDate && earliestDate !== earliestAvailableDate) {
        offerPageDispatch({
          type: OfferPageActions.setDepartureMonth,
          month: { month: earliestAvailableDate.getMonth(), year: earliestAvailableDate.getFullYear() },
        })
      }

      if (checkoutItems?.length) {
        const offerCheckoutItems = checkoutItems?.filter((item) => item.offerId === offer.id) ?? []

        const roomOccupants = offerCheckoutItems.map(item => generateRoomOccupants(item.occupancy))

        if (roomOccupants.length) {
          offerPageDispatch({
            type: OfferPageActions.setRooms,
            rooms: roomOccupants,
          })
        }
      }

      dispatch(tourV2OfferProductDetailView(offer))

      if (ENABLED_OFFER_TYPES_RECENTLY_VIEWED.has(offer.type) && !isPrivateRequest) {
        dispatch(offerViewed(offer.id, offer.type))
      }

      Analytics.trackClientEvent({
        subject: 'click-tour-offer',
        action: 'clicked',
        category: 'logging',
        type: 'operational',
        optimizelyEventId: '25460370151',
        optimizelyEventKey: 'click-tour-offer',
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer, isPrivateRequest])
  if (offerError) {
    if (offerError?.status == 404) {
      return <Error404PageWithOffers />
    } else {
      return <Error500Page />
    }
  }

  if (isOfferLoading || !offer || !offerPageState.tourV2Offer) {
    return <LoadingIndicator />
  }

  if (offer) {
    // if URL use the tour option id then redirect to the url using the tour id (slug)
    if (!offer.variations[variationId]) {
      const matchedVariationByTourOptionId = Object.values(offer.variations).find(variation => variation.fkTourOptionId === variationId)

      if (matchedVariationByTourOptionId) {
        const toUrl = `/${geo.currentRegionCode.toLowerCase()}/tour/${offer.id}/${matchedVariationByTourOptionId.id}`
        return <RedirectWithStatus code={301} to={toUrl} />
      }

      // if slug variation not found we redirect to default tour offer page
      const toUrl = `/${geo.currentRegionCode.toLowerCase()}/tour/${offer.id}`
      return <Redirect to={toUrl} />
    }
  }

  return <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
    <OfferPageStateProvider state={offerPageState} stateSnapshotRef={stateSnapshotRef} dispatch={offerPageDispatch}>
      <OfferPageWrapper className="OfferPage">
        {!!offerPageState.tourV2Offer && <TourV2Content
          shareModalConfig={shareModalConfig}
          key={offer.id}
        />}
      </OfferPageWrapper>
    </OfferPageStateProvider>
  </AnalyticsPageContext.Provider>
}

export default withRouter(connect<MappedStateProps, unknown, Props, App.State>((state, ownProps) => {
  const { offerId } = ownProps.match.params
  const query = ownProps.location.search

  const privateRequestKey = new URLSearchParams(query).get('privateRequestKey')
  const offerKey = privateRequestKey ? `${offerId}-${privateRequestKey}` : offerId

  const offer = state.offer.tourV2Offers[offerKey]

  return {
    checkoutItems: getTourV2CartItems(state),
    isOfferLoading: state.offer.offersLoading[offerKey],
    offer,
    offerError: state.offer.offerErrors[offerKey],
    shareModalConfig: getTourV2ShareModalConfig(state, offer),
    currency: state.geo.currentCurrency,
    isPrivateRequest: !!privateRequestKey,
  }
})(TourV2OfferVariationPage))
